<template>
  <v-container fluid class="pa-0">
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center flex-wrap">
        <div>
          <div class="text-h6">Monthly Fee Projections</div>
          <div class="subtitle-2 grey--text">
            {{ monthLabel }}
          </div>
        </div>
        <div class="d-flex align-center flex-wrap flex-grow-1 ml-10">
          <div class="d-flex flex-wrap stat-cards">
            <v-card flat class="stat-card mr-3 mb-3" outlined>
              <div class="stat-card-label">Low</div>
              <div class="stat-card-value">
                {{ formatCurrency(totals.low) }}
              </div>
            </v-card>
            <v-card flat class="stat-card mr-3 mb-3" outlined>
              <div class="stat-card-label">High</div>
              <div class="stat-card-value">
                {{ formatCurrency(totals.high) }}
              </div>
            </v-card>
            <v-card flat class="stat-card mr-3 mb-3" outlined>
              <div class="stat-card-label">Actual</div>
              <div class="stat-card-value">
                {{ formatCurrency(totals.actual) }}
              </div>
            </v-card>
            <v-card flat class="stat-card mr-3 mb-3" outlined>
              <div class="stat-card-label">Remaining</div>
              <div class="stat-card-value">
                {{ formatCurrency(totals.remaining) }}
              </div>
            </v-card>
          </div>
          <div class="month-picker ml-auto">
            <v-menu
              v-model="monthMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="monthLabel"
                  label="Month"
                  prepend-icon="mdi-calendar"
                  readonly
                  dense
                  outlined
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="monthPicker"
                type="month"
                no-title
                scrollable
                @input="onMonthChange"
              />
            </v-menu>
          </div>
        </div>
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="rows"
        :items-per-page="-1"
        dense
        disable-sort
        class="elevation-0"
        :loading="loading"
        hide-default-footer
      >
        <template v-slot:item.name="{ item }">
          <div class="d-flex align-center">
            <span>{{ item.name }}</span>
            <v-chip
              v-if="!item.hasProjection"
              x-small
              color="warning"
              class="ml-2"
              outlined
            >
              Waiting
            </v-chip>
          </div>
        </template>
        <template v-slot:item.staff_codes="{ item }">
          {{ item.staff_codes.join(", ") }}
        </template>
        <template v-slot:item.low="{ item }">
          {{ formatCurrency(item.low) }}
        </template>
        <template v-slot:item.high="{ item }">
          {{ formatCurrency(item.high) }}
        </template>
        <template v-slot:item.actual="{ item }">
          {{ formatCurrency(item.actual) }}
        </template>
        <template v-slot:item.remaining="{ item }">
          {{ formatCurrency(Math.max(0, item.high - item.actual)) }}
        </template>
        <template v-slot:body.append>
          <tr class="font-weight-medium">
            <td>Total</td>
            <td></td>
            <td>{{ formatCurrency(totals.low) }}</td>
            <td>{{ formatCurrency(totals.high) }}</td>
            <td>{{ formatCurrency(totals.actual) }}</td>
            <td>{{ formatCurrency(totals.remaining) }}</td>
          </tr>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import moment from "moment";
import gql from "graphql-tag";
import FeeProjectionsQuery from "@/graphql/FeeProjections.gql";
import FeesByAttorneyByMonth from "@/graphql/FeesByAttorneyByMonth.gql";

const currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2,
});

export default {
  name: "OrgFeeProjection",
  data() {
    return {
      loading: false,
      monthMenu: false,
      monthPicker: moment().format("YYYY-MM"),
      orgUsers: [],
      fees: [],
      projections: {},
      headers: [
        { text: "Attorney", value: "name" },
        { text: "Staff Code(s)", value: "staff_codes" },
        { text: "Low", value: "low" },
        { text: "High", value: "high" },
        { text: "Actual", value: "actual" },
        { text: "Remaining", value: "remaining" },
      ],
    };
  },
  computed: {
    orgId() {
      return this.$route.params.id;
    },
    monthStart() {
      return moment(this.monthPicker, "YYYY-MM").startOf("month");
    },
    monthEnd() {
      return moment(this.monthPicker, "YYYY-MM").endOf("month");
    },
    monthLabel() {
      return this.monthStart.format("MMMM YYYY");
    },
    rows() {
      const rows = this.orgUsers.map((u) => {
        const staffCodes = this.normalizeAndExpandStaffCodes(u.staffCode);
        const projection = this.projections[u.id] || {
          low: 0,
          high: 0,
          has: false,
        };
        const actual = this.sumFeesForCodes(staffCodes);
        return {
          id: u.id,
          name: `${u.first_name} ${u.last_name}`,
          staff_codes: staffCodes,
          low: projection.low,
          high: projection.high,
          actual,
          hasProjection: projection.has,
        };
      });

      // Include any fee-only attorneys not already listed
      const listedStaff = new Set(rows.flatMap((r) => r.staff_codes));
      const extra = [];
      this.fees.forEach((fee) => {
        const feeCodes = this.parseCodes(fee.attorney);
        const key = feeCodes.find((c) => !listedStaff.has(c));
        if (key) {
          extra.push({
            id: `fee-${fee.attorney}`,
            name: fee.attorney,
            staff_codes: feeCodes,
            low: 0,
            high: 0,
            actual: this.sumFeesForCodes(feeCodes),
            hasProjection: false,
          });
          feeCodes.forEach((c) => listedStaff.add(c));
        }
      });

      const excludedCodes = new Set(["CS", "ECD", "WDM"]);
      return rows
        .concat(extra)
        .filter((r) => !r.staff_codes.some((c) => excludedCodes.has(c)))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    totals() {
      return this.rows.reduce(
        (acc, r) => {
          acc.low += Number(r.low || 0);
          acc.high += Number(r.high || 0);
          acc.actual += Number(r.actual || 0);
          acc.remaining += Math.max(0, Number(r.high || 0) - Number(r.actual || 0));
          return acc;
        },
        { low: 0, high: 0, actual: 0, remaining: 0 }
      );
    },
  },
  watch: {
    orgId: "fetchData",
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    onMonthChange(val) {
      this.monthPicker = val;
      this.monthMenu = false;
      this.fetchData();
    },
    formatCurrency(val) {
      return currency.format(Number(val || 0));
    },
    parseCodes(str) {
      return (str || "")
        .toUpperCase()
        .split(/[^A-Z0-9]+/)
        .map((c) => c.trim())
        .filter((c) => c.length > 0);
    },
    normalizeAndExpandStaffCodes(staffCode) {
      const tokens = this.parseCodes(staffCode);
      const groups = [
        ["CLP", "PPR"],
        ["ACS", "TRS", "RAS"],
        ["LS", "KNP", "EMR"],
        ["RCS", "PL", "MM", "SSD", "WC"],
        ["NPT", "NT"],
      ];
      const expanded = tokens.flatMap((code) => {
        const match = groups.find((g) => g.includes(code));
        return match ? match : [code];
      });
      return Array.from(new Set(expanded));
    },
    sumFeesForCodes(codes) {
      const codeSet = new Set(codes);
      return this.fees
        .filter((f) => this.parseCodes(f.attorney).some((c) => codeSet.has(c)))
        .reduce((acc, f) => acc + Number(f.total || 0), 0);
    },
    async fetchData() {
      this.loading = true;
      try {
        await this.fetchOrganizationUsers();
        await Promise.all([this.fetchFees(), this.fetchProjectionsForUsers()]);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.loading = false;
      }
    },
    async fetchOrganizationUsers() {
      const { data } = await this.$apollo.query({
        query: gql`
          query($id: ID!) {
            organization(id: $id) {
              id
              users {
                id
                first_name
                last_name
                staffCode
                departments {
                  name
                }
              }
            }
          }
        `,
        variables: { id: this.orgId },
        fetchPolicy: "network-only",
      });

      const targetDepartments = new Set([
        "Litigation Attorneys",
        "Pre-Litigation Attorneys",
      ]);

      const users = (data.organization.users || []).filter((u) => {
        const deptNames = (u.departments || []).map((d) => d.name);
        return deptNames.some((n) => targetDepartments.has(n));
      });

      this.orgUsers = users;
    },
    async fetchFees() {
      const { data } = await this.$apollo.query({
        query: FeesByAttorneyByMonth,
        variables: {
          startDate: this.monthStart.format("YYYY-MM-DD"),
          endDate: this.monthEnd.format("YYYY-MM-DD"),
        },
        fetchPolicy: "network-only",
      });
      this.fees = data.fees_in_period_by_attorney_by_month || [];
    },
    async fetchProjectionsForUsers() {
      const userIds = this.orgUsers.map((u) => u.id);
      const projectionsMap = {};

      await Promise.all(
        userIds.map(async (uid) => {
          const { data } = await this.$apollo.query({
            query: FeeProjectionsQuery,
            variables: {
              userId: uid,
              month: this.monthStart.format("YYYY-MM-DD"),
            },
            fetchPolicy: "network-only",
          });
          const proj = (data.attorney_fee_projections || [])[0];
          if (proj) {
            projectionsMap[uid] = {
              low: Number(proj.low_estimate || 0),
              high: Number(proj.high_estimate || 0),
              has: true,
            };
          } else {
            projectionsMap[uid] = { low: 0, high: 0, has: false };
          }
        })
      );

      this.projections = projectionsMap;
    },
  },
};
</script>

<style scoped>
.stats-chips {
  align-items: center;
}
.month-picker {
  min-width: 220px;
}
.stat-cards {
  align-items: stretch;
}
.stat-card {
  min-width: 140px;
  padding: 12px 14px;
  border-radius: 8px;
}
.stat-card-label {
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.6);
  font-weight: 500;
}
.stat-card-value {
  font-size: 1.4rem;
  font-weight: 600;
  line-height: 1.2;
}
</style>
